import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { businessService } from '../../shared/services';

export const SubscriptionPending: React.FC = () => {
  const navigate = useNavigate();
  const [showPending, setShowPending] = useState(false);
  const [manualChecking, setManualChecking] = useState(false);
  const attemptsRef = useRef(0);
  const businessIdRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Parse businessId from URL once
    const params = new URLSearchParams(window.location.search);
    const externalRef = params.get('external_reference');
    const id = externalRef ? Number(externalRef.split(':')[0]) : null;

    if (!id || isNaN(id)) {
      setShowPending(true);
      return;
    }

    businessIdRef.current = id;

    const checkSubscription = async () => {
      const bid = businessIdRef.current;
      if (!bid) return;

      try {
        const business = await businessService.getById(bid);
        if (cancelled) return;

        if (business.subscription && (business.subscription.status === 'Active' || business.subscription.isTrialing)) {
          navigate('/business', { replace: true });
          return;
        }
      } catch {
        // Business not found — just show pending
      }

      if (cancelled) return;

      attemptsRef.current += 1;
      if (attemptsRef.current >= 3) {
        setShowPending(true);
        return;
      }

      // Try again after 3s (IPN may still be arriving)
      setTimeout(checkSubscription, 3000);
    };

    // Wait 2s before first check to give IPN time to arrive
    const initialTimer = setTimeout(checkSubscription, 2000);

    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
    };
  }, [navigate]);

  const handleManualRetry = async () => {
    setManualChecking(true);
    const bid = businessIdRef.current;
    if (!bid) {
      setManualChecking(false);
      return;
    }

    try {
      const business = await businessService.getById(bid);
      if (business.subscription && (business.subscription.status === 'Active' || business.subscription.isTrialing)) {
        navigate('/business', { replace: true });
        return;
      }
    } catch {
      // ignore
    }

    attemptsRef.current = 0; // reset so auto retry can work again
    setManualChecking(false);
  };

  if (!showPending) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <Box textAlign="center">
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Verificando pagamento...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="background.default"
      padding={3}
    >
      <Paper
        sx={{
          p: 6,
          maxWidth: 500,
          textAlign: 'center',
          borderRadius: 3,
        }}
      >
        <HourglassEmptyIcon color="warning" sx={{ fontSize: 80, mb: 2 }} />
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Pagamento Pendente
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          Sua assinatura está aguardando confirmação de pagamento.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Assim que o pagamento for confirmado, você receberá um email e poderá
          acessar todas as funcionalidades. Isso pode levar alguns minutos.
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <Button
            variant="contained"
            onClick={handleManualRetry}
            disabled={manualChecking}
            startIcon={manualChecking ? <CircularProgress size={18} /> : <RefreshIcon />}
            sx={{ borderRadius: 2, py: 1.5 }}
          >
            {manualChecking ? 'Verificando...' : 'Verificar novamente'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            sx={{ borderRadius: 2 }}
          >
            Voltar para o início
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
